/**
 * Generate complete calendar date range including days with zero activity.
 *
 * @param startDate - Start date in YYYY-MM-DD format
 * @param endDate - End date in YYYY-MM-DD format
 * @returns Array of date strings in YYYY-MM-DD format
 * @throws Error if date strings are invalid or end is before start
 */
export function generateCalendarDates(startDate: string, endDate: string): string[] {
  // Security Issue #6: Validate date inputs
  const datePattern = /^\d{4}-\d{2}-\d{2}$/;
  if (!datePattern.test(startDate) || !datePattern.test(endDate)) {
    throw new Error('Invalid date format. Expected YYYY-MM-DD');
  }

  const dates: string[] = [];

  // Critical Bug #2: Use UTC to avoid timezone drift
  // Parse YYYY-MM-DD as UTC instead of local time
  const startParts = startDate.split('-').map(Number);
  const endParts = endDate.split('-').map(Number);

  const startYear = startParts[0]!;
  const startMonth = startParts[1]!;
  const startDay = startParts[2]!;
  const endYear = endParts[0]!;
  const endMonth = endParts[1]!;
  const endDay = endParts[2]!;

  const start = new Date(Date.UTC(startYear, startMonth - 1, startDay));
  const end = new Date(Date.UTC(endYear, endMonth - 1, endDay));

  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    throw new Error('Invalid date values');
  }

  if (start > end) {
    throw new Error('Start date must be before or equal to end date');
  }

  const current = new Date(start);

  while (current <= end) {
    // Format as YYYY-MM-DD using UTC methods
    const year = current.getUTCFullYear();
    const month = String(current.getUTCMonth() + 1).padStart(2, '0');
    const day = String(current.getUTCDate()).padStart(2, '0');
    dates.push(`${year}-${month}-${day}`);

    // Critical Bug #1: Use millisecond arithmetic instead of setDate()
    // to prevent infinite loops when crossing month boundaries
    current.setTime(current.getTime() + 86400000); // Add 24 hours in milliseconds
  }

  return dates;
}
